import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Shield,
  Bot,
  Sliders,
  Save,
  RotateCcw,
  CheckCircle2,
  AlertTriangle,
  Lock,
  Cpu,
  Sparkles,
  Zap,
  Activity,
  Layers,
  Settings,
  HelpCircle,
  FileText,
  UserCheck,
  Building2,
  Store,
  Smartphone,
  Globe,
  Crown,
  Eye,
  Scale,
  Briefcase
} from 'lucide-react';

interface LunaModeConfigData {
  lunaMode: string;
  displayName: string;
  description: string;
  status: 'ACTIVE' | 'RESTRICTED' | 'MAINTENANCE' | 'DISABLED';
  aiModel: string;
  callKeySource?: string;
  callAiModel?: string | null;
  creatorInfo: string;
  ownershipInfo: string;
  capabilitiesInstructions: string;
  restrictionsInstructions: string;
  customSystemPrompt: string;
  maxOutputTokens: number;
  temperature: number;
  allowedPermissions: Record<string, boolean>;
}

interface LunaMasterConfigHubProps {
  isOpen: boolean;
  onClose: () => void;
  authHeader: () => Record<string, string>;
  isInline?: boolean;
}

const LUNA_MODES = [
  { id: 'master_admin', label: 'Master Admin', icon: Crown, badge: 'GOD', color: 'from-purple-500 to-indigo-600' },
  { id: 'pos', label: 'POS Luna', icon: Store, badge: 'POS', color: 'from-emerald-500 to-teal-600' },
  { id: 'public', label: 'Public Luna', icon: Globe, badge: 'PUBLIC', color: 'from-cyan-500 to-blue-600' },
  { id: 'personal', label: 'Personal Luna', icon: Smartphone, badge: 'MOBILE', color: 'from-pink-500 to-rose-600' },
  { id: 'corporate', label: 'Corporate Luna', icon: Scale, badge: 'CORP', color: 'from-teal-500 to-emerald-600' },
  { id: 'corporate_admin', label: 'Corporate Firm', icon: Briefcase, badge: 'FIRM', color: 'from-blue-500 to-indigo-600' },
  { id: 'tenet', label: 'Tenet Luna', icon: Building2, badge: 'TENET', color: 'from-amber-500 to-orange-600' },
  { id: 'tenet_admin', label: 'Tenet Admin', icon: Layers, badge: 'FRANCHISE', color: 'from-violet-500 to-purple-700' },
];

const modelPrettyName = (id: string): string =>
  id
    .replace(/^gemini-/i, 'Gemini ')
    .replace(/^gemma-/i, 'Gemma ')
    .replace(/^lyria-/i, 'Lyria ')
    .replace(/^nano-banana/i, 'Nano Banana')
    .replace(/^antigravity-/i, 'Antigravity ')
    .replace(/^deep-research-/i, 'Deep Research ')
    .split('-')
    .map(w => (/^\d/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');

/** [id, budget badge, spec line] — every chat-capable model available to the platform key */
const MODEL_TUPLES: Array<[string, string, string]> = [
  // ── Live / Voice Cores ──
  ['gemini-3.8-live-extended-thinking', 'LIVE • THINKING', '128K ctx • Extended Thinking Audio'],
  ['gemini-3.5-live-translate-preview', 'LIVE', '16K ctx • Live Translate'],
  ['gemini-3.5-transcribe-live', 'LIVE', '128K ctx • Live Transcribe'],
  // ── Text Chat: flagship to budget ──
  ['gemini-3.8-flash', 'BALANCED • FAST', '1M ctx • Text Chat'],
  ['gemini-3.7-flash', 'BALANCED • FAST', '1M ctx • Text Chat'],
  ['gemini-3.6-flash', 'BALANCED • FAST', '1M ctx • Text Chat'],
  ['gemini-3.5-flash', 'BALANCED • FAST', '1M ctx • Text Chat'],
  ['gemini-3.5-flash-lite', 'BUDGET', '1M ctx • Text Chat Lite'],
  ['gemini-3.1-flash-lite', 'BUDGET', '1M ctx • Text Chat Lite'],
  ['gemini-3.1-flash-lite-preview', 'BUDGET', '1M ctx • Text Chat Lite Preview'],
  ['gemini-3-flash-preview', 'BALANCED', '1M ctx • Text Chat Preview'],
  ['gemini-3.1-pro-preview', 'PREMIUM', '1M ctx • Deep Reasoning'],
  ['gemini-3.1-pro-preview-customtools', 'PREMIUM', '1M ctx • Custom Tools'],
  ['gemini-flash-latest', 'BALANCED', '1M ctx • Rolling Latest'],
  ['gemini-flash-lite-latest', 'BUDGET', '1M ctx • Rolling Latest'],
  ['gemini-pro-latest', 'PREMIUM', '1M ctx • Rolling Latest'],
  ['gemini-omni-1.1-flash', 'BALANCED', '128K ctx • Omni Multimodal'],
  ['gemini-omni-flash-preview', 'BALANCED', '128K ctx • Omni Preview'],
  ['gemma-4-26b-a4b-it', 'BUDGET', '262K ctx • Open Weights'],
  ['gemma-4-31b-it', 'BUDGET', '262K ctx • Open Weights'],
  // ── Specialized Agents / Research ──
  ['gemini-robotics-er-2-preview', 'SPECIAL', '128K ctx • Robotics ER'],
  ['gemini-robotics-er-2-streaming-preview', 'SPECIAL', '128K ctx • Robotics Streaming'],
  ['antigravity-preview-05-2026', 'SPECIAL', '1M ctx • Agentic Preview'],
  ['antigravity-preview-09-2026', 'SPECIAL', '1M ctx • Agentic Preview'],
  ['antigravity-preview-latest', 'SPECIAL', '1M ctx • Agentic Latest'],
  ['deep-research-max-preview-04-2026', 'SPECIAL', '128K ctx • Deep Research Max'],
  ['deep-research-preview-04-2026', 'SPECIAL', '128K ctx • Deep Research'],
  ['deep-research-pro-preview-12-2025', 'SPECIAL', '128K ctx • Deep Research Pro'],
  ['gemini-3.5-transcribe', 'SPECIAL', '98K ctx • Audio Transcribe'],
  // ── Image Generation & Vision ──
  ['nano-banana-pro-preview', 'IMAGE', '128K ctx • Image Gen Pro'],
  ['gemini-3-pro-image', 'IMAGE', '128K ctx • Image Gen'],
  ['gemini-3-pro-image-preview', 'IMAGE', '128K ctx • Image Gen Preview'],
  ['gemini-3.1-flash-image', 'IMAGE', '64K ctx • Image Gen Flash'],
  ['gemini-3.1-flash-image-preview', 'IMAGE', '64K ctx • Image Gen Flash'],
  ['gemini-3.1-flash-lite-image', 'IMAGE', '64K ctx • Image Gen Lite'],
  // ── Speech & Music Generation ──
  ['gemini-3.1-flash-tts-preview', 'AUDIO', '8K ctx • Text-to-Speech'],
  ['gemini-3.8-flash-tts', 'AUDIO', '8K ctx • Text-to-Speech'],
  ['gemini-3.8-flash-lite-tts', 'AUDIO', '8K ctx • Text-to-Speech Lite'],
  ['lyria-3-clip-preview', 'MUSIC', '1M ctx • Music Gen Clip'],
  ['lyria-3-pro-preview', 'MUSIC', '1M ctx • Music Gen Pro'],
  ['lyria-3.5', 'MUSIC', '1M ctx • Music Gen'],
];

const AVAILABLE_MODELS = [
  {
    id: 'gemini-3.8-live',
    name: 'Gemini 3.8 Live (Thinking Audio & Multimodal Core)',
    badge: 'NEW • ULTRA SMART',
    speed: '1.18s First-Word • Thinking Audio',
    desc: 'Native audio architecture with continuous background thinking, asynchronous tools & 97+ languages'
  },
  {
    id: 'gemini-3.1-flash-live-preview',
    name: 'Gemini 3.1 Flash Live Preview (Sovereign Fast Core)',
    badge: 'HIGH SPEED',
    speed: 'Ultra-Low Latency • Fast Stream',
    desc: 'Low-latency sovereign live core for rapid voice and multimodal streaming'
  },
  ...MODEL_TUPLES.map(([id, badge, speed]) => ({ id, name: modelPrettyName(id), badge, speed, desc: '' })),
  { id: 'mimo-v2.6-flash', name: 'Xiaomi MiMo V2.6 Flash', badge: 'MIMO • BUDGET', speed: 'Ultra-Low Cost • Cache-Hit Pricing', desc: 'Xiaomi MiMo V2.6 Flash — OpenAI-compatible provider, strongest $/quality for text chat' },
  { id: 'mimo-v2.6-pro', name: 'Xiaomi MiMo V2.6 Pro', badge: 'MIMO • PRO', speed: 'High Intelligence • Low Cost', desc: 'Xiaomi MiMo V2.6 Pro — top MiMo reasoning at budget-tier pricing' },
  { id: 'mimo-v2.5-pro', name: 'Xiaomi MiMo V2.5 Pro (legacy)', badge: 'MIMO • LEGACY', speed: 'V2.5 Generation', desc: 'Legacy V2.5 Pro — deprecation slated, prefer V2.6' },
  { id: 'mimo-v2.5', name: 'Xiaomi MiMo V2.5 (legacy)', badge: 'MIMO • LEGACY', speed: 'V2.5 Generation', desc: 'Legacy V2.5 — deprecation slated, prefer V2.6' },
];

const PERMISSION_FLAGS = [
  { key: 'canAccessInventory', label: 'Read/Write Inventory', desc: 'Allows looking up stock, updating inventory counts' },
  { key: 'canAccessSales', label: 'Read Sales & Orders', desc: 'Allows reporting on daily transaction records' },
  { key: 'canAccessFinancials', label: 'Financials & Expenses', desc: 'Access to revenue, margins, and expense logging' },
  { key: 'canModifyPrices', label: 'Price Modification', desc: 'Allows changing live product prices in DB' },
  { key: 'canUseVoiceBilling', label: 'Voice Billing & Fast Stock', desc: 'Enables rapid voice billing and barcode audit modes' },
  { key: 'canAccessPublicDirectory', label: 'Public Directory Search', desc: 'Allows location-based store and product lookup' },
  { key: 'canSetReminders', label: 'Reminders & Notes', desc: 'Enables personal tasks, reminders, and voice notes' },
  { key: 'canAccessGlobalAnalytics', label: 'Global System Analytics', desc: 'Access to token usage ledgers and session audits' },
  { key: 'canManageUsers', label: 'User Governance', desc: 'Allows managing user accounts and RBAC roles' },
];

export const LunaMasterConfigHub: React.FC<LunaMasterConfigHubProps> = ({ isOpen, onClose, authHeader, isInline }) => {
  const [configs, setConfigs] = useState<Record<string, LunaModeConfigData>>({});
  const [activeMode, setActiveMode] = useState<string>('master_admin');
  const [providerFilter, setProviderFilter] = useState<'all' | 'gemini' | 'mimo'>('all');

  const getSelectableModels = (filter: string, currentVal: string) => {
    const list = filter === 'all'
      ? AVAILABLE_MODELS
      : filter === 'mimo'
        ? AVAILABLE_MODELS.filter(m => m.id.startsWith('mimo'))
        : AVAILABLE_MODELS.filter(m => !m.id.startsWith('mimo'));
    
    // Safety guarantee: If currentVal is not in the filtered list, include it so the browser never corrupts or desyncs the select!
    if (currentVal && !list.some(m => m.id === currentVal)) {
      const found = AVAILABLE_MODELS.find(m => m.id === currentVal);
      if (found) {
        return [found, ...list];
      }
    }
    return list;
  };

  const [selectedGlobalModel, setSelectedGlobalModel] = useState<string>('gemini-3.8-live');
  const [persistedGlobalModel, setPersistedGlobalModel] = useState<string>('gemini-3.8-live');
  const [selectedCallModel, setSelectedCallModel] = useState<string>('gemini-3.8-live');
  const [persistedCallModel, setPersistedCallModel] = useState<string>('gemini-3.8-live');
  const [applyingGlobal, setApplyingGlobal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showFullPromptPreview, setShowFullPromptPreview] = useState<boolean>(false);

  const handleSelectGlobalModel = (val: string) => {
    setSelectedGlobalModel(val);
    if (val.startsWith('mimo')) {
      setProviderFilter('mimo');
    } else if (providerFilter === 'mimo') {
      setProviderFilter('all');
    }
    setConfigs(prev => {
      const updated = { ...prev };
      if (updated['master_admin']) {
        updated['master_admin'] = { ...updated['master_admin'], aiModel: val };
      }
      if (updated[activeMode]) {
        updated[activeMode] = { ...updated[activeMode], aiModel: val };
      }
      return updated;
    });
  };

  const handleProviderChange = (newProvider: 'all' | 'gemini' | 'mimo') => {
    setProviderFilter(newProvider);
    if (newProvider === 'mimo') {
      if (!selectedGlobalModel.startsWith('mimo')) {
        handleSelectGlobalModel('mimo-v2.6-flash');
      }
    } else if (newProvider === 'gemini') {
      if (selectedGlobalModel.startsWith('mimo')) {
        handleSelectGlobalModel('gemini-3.8-live');
      }
    }
  };

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/luna/configs', { headers: authHeader() });
      const data = await res.json();
      if (data.success && Array.isArray(data.configs)) {
        const map: Record<string, LunaModeConfigData> = {};
        data.configs.forEach((c: LunaModeConfigData) => {
          map[c.lunaMode] = c;
        });
        setConfigs(map);

        // Authoritative resolution: read master_admin first as ecosystem sovereign root
        const masterConfig = data.configs.find((c: any) => c.lunaMode === 'master_admin') || data.configs[0];
        const currentActive = masterConfig?.aiModel || 'gemini-3.8-live';
        const currentCall = masterConfig?.callAiModel || 'gemini-3.8-live';

        setSelectedGlobalModel(currentActive);
        setPersistedGlobalModel(currentActive);
        setSelectedCallModel(currentCall);
        setPersistedCallModel(currentCall);

        // Automatically align provider filter to the active model
        if (currentActive.startsWith('mimo')) {
          setProviderFilter('mimo');
        } else {
          setProviderFilter('all');
        }
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Failed to load Luna configurations' });
    }
    setLoading(false);
  }, [authHeader]);

  const handleApplyGlobalModel = async (modelToApply?: string) => {
    const targetModel = modelToApply || selectedGlobalModel;
    setApplyingGlobal(true);
    setMessage(null);
    try {
      const res = await fetch('/api/admin/luna/configs/apply-global-model', {
        method: 'POST',
        headers: {
          ...authHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          aiModel: targetModel,
          callKeySource: activeConfig.callKeySource === 'primary' ? 'primary' : 'training',
          callAiModel: selectedCallModel
        }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage({
          type: 'success',
          text: `⚡ Global Base Model successfully set to '${modelPrettyName(targetModel)}' across all 8 Luna operational modes (Public, POS, Personal, Tenet & Admin)!`
        });
        setPersistedGlobalModel(targetModel);
        setPersistedCallModel(selectedCallModel);
        if (targetModel.startsWith('mimo')) {
          setProviderFilter('mimo');
        }
        setConfigs(prev => {
          const updated: Record<string, LunaModeConfigData> = {};
          Object.keys(prev).forEach(modeKey => {
            updated[modeKey] = {
              ...prev[modeKey],
              aiModel: targetModel,
              callAiModel: selectedCallModel,
            };
          });
          return updated;
        });
        await fetchConfigs();
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to apply global model' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Network error applying global model' });
    }
    setApplyingGlobal(false);
  };

  useEffect(() => {
    if (isOpen || isInline) {
      fetchConfigs();
    }
  }, [isOpen, isInline]);

  if (!isOpen && !isInline) return null;

  const activeConfig = configs[activeMode] || {
    lunaMode: activeMode,
    displayName: LUNA_MODES.find(m => m.id === activeMode)?.label || activeMode,
    description: '',
    status: 'ACTIVE',
    aiModel: selectedGlobalModel || 'gemini-3.8-live',
    callKeySource: 'training',
    callAiModel: selectedCallModel || 'gemini-3.8-live',
    creatorInfo: '',
    ownershipInfo: '',
    capabilitiesInstructions: '',
    restrictionsInstructions: '',
    customSystemPrompt: '',
    maxOutputTokens: 500,
    temperature: 0.7,
    allowedPermissions: {},
  };

  const handleUpdateActiveConfig = (field: keyof LunaModeConfigData, value: any) => {
    if (activeMode === 'master_admin') {
      if (field === 'aiModel') setSelectedGlobalModel(value);
      if (field === 'callAiModel') setSelectedCallModel(value);
    }
    setConfigs(prev => ({
      ...prev,
      [activeMode]: {
        ...activeConfig,
        [field]: value,
      }
    }));
  };

  const handleTogglePermission = (key: string) => {
    const currentPerms = activeConfig.allowedPermissions || {};
    handleUpdateActiveConfig('allowedPermissions', {
      ...currentPerms,
      [key]: !currentPerms[key],
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      // If saving from master_admin OR if the top global models were edited,
      // apply them globally so saving never loses the model chosen in the top dropdown!
      const shouldApplyGlobally =
        activeMode === 'master_admin' ||
        selectedGlobalModel !== persistedGlobalModel ||
        selectedCallModel !== persistedCallModel;

      if (shouldApplyGlobally) {
        const globalRes = await fetch('/api/admin/luna/configs/apply-global-model', {
          method: 'POST',
          headers: {
            ...authHeader(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            aiModel: selectedGlobalModel,
            callKeySource: activeConfig.callKeySource === 'primary' ? 'primary' : 'training',
            callAiModel: selectedCallModel,
          }),
        });
        const globalData = await globalRes.json();
        if (!globalData.success) {
          setMessage({ type: 'error', text: globalData.error || 'Failed to apply global model' });
          setSaving(false);
          return;
        }
      }

      const res = await fetch(`/api/admin/luna/configs/${activeMode}`, {
        method: 'PUT',
        headers: {
          ...authHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...activeConfig,
          aiModel: shouldApplyGlobally ? selectedGlobalModel : (activeConfig.aiModel || selectedGlobalModel),
          callAiModel: shouldApplyGlobally ? selectedCallModel : (activeConfig.callAiModel || selectedCallModel),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage({
          type: 'success',
          text: `Successfully saved '${activeConfig.displayName}' & AI Model (${modelPrettyName(selectedGlobalModel)})!`
        });
        setPersistedGlobalModel(selectedGlobalModel);
        setPersistedCallModel(selectedCallModel);
        if (selectedGlobalModel.startsWith('mimo')) {
          setProviderFilter('mimo');
        }
        await fetchConfigs();
        setTimeout(() => setMessage(null), 3500);
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to save configuration' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Network error saving configuration' });
    }
    setSaving(false);
  };

  const handleResetDefaults = async () => {
    if (!window.confirm('Reset all 6 Luna configurations to factory default instructions?')) return;
    setSaving(true);
    try {
      const res = await fetch('/api/admin/luna/configs/seed', {
        method: 'POST',
        headers: authHeader(),
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Reset all configurations to factory defaults!' });
        fetchConfigs();
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Error resetting configurations' });
    }
    setSaving(false);
  };

  const content = (
    <div className={`bg-[#0b0f19] border border-cyan-500/30 rounded-2xl w-full flex flex-col overflow-hidden shadow-[0_0_80px_rgba(6,182,212,0.15)] ${isInline ? '' : 'max-w-6xl max-h-[92vh]'}`}>

        {/* Header Bar */}
        <div className="px-6 py-4 border-b border-cyan-500/20 bg-black/60 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/40 flex items-center justify-center shadow-[0_0_15px_rgba(6,182,212,0.3)]">
              <Bot className="text-cyan-400" size={22} />
            </div>
            <div>
              <h2 className="text-lg font-black text-white tracking-wide flex items-center gap-2 font-mono">
                LUNA MASTER CONFIG HUB
                <span className="px-2 py-0.5 rounded text-[10px] bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 font-mono">LIVE MATRIX</span>
              </h2>
              <p className="text-xs text-slate-400 font-mono">Manage capabilities, hardcoded prompts & identity rules per Luna mode in real-time</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleResetDefaults}
              className="px-3 py-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-300 text-xs font-bold hover:bg-amber-500/20 transition-colors flex items-center gap-1.5"
              title="Reset all prompts & rules to initial factory defaults"
            >
              <RotateCcw size={13} /> Reset Factory Defaults
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-lg border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white hover:border-slate-500 transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Banner Message */}
        {message && (
          <div className={`px-6 py-2.5 text-xs font-bold flex items-center justify-between ${message.type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border-b border-emerald-500/30' : 'bg-rose-500/20 text-rose-300 border-b border-rose-500/30'}`}>
            <span className="flex items-center gap-2">
              {message.type === 'success' ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
              {message.text}
            </span>
            <button onClick={() => setMessage(null)} className="text-white/60 hover:text-white">✕</button>
          </div>
        )}

        {/* 🌐 GLOBAL SYSTEM-WIDE BASE AI MODEL SELECTOR (Master Admin Sovereign Governance) */}
        <div className="px-6 py-4 border-b border-cyan-500/20 bg-gradient-to-r from-cyan-950/40 via-purple-950/30 to-black/60 shrink-0">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2 h-2 rounded-full bg-cyan-400 animate-ping" />
                <h3 className="text-xs font-black text-cyan-300 font-mono tracking-wider uppercase flex items-center gap-2">
                  <Cpu size={14} className="text-cyan-400" />
                  Global Base AI Model Selector (Entire Ecosystem)
                </h3>
                <span className="px-2 py-0.5 rounded text-[10px] bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 font-mono">
                  SUPREME GOVERNANCE
                </span>
              </div>
              <p className="text-[11px] text-slate-400 font-mono">
                Switch base live conversational engine globally across all Luna agents: Public, POS, Personal Mobile, and Tenet WhatsApp/SIP.
              </p>
            </div>

            {/* ═══ TWO INDEPENDENT KEY PROFILES — each button keeps its OWN saved model ═══ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              {/* ── Profile 1: PRIMARY CORE KEY (PAID) ── */}
              <div className="rounded-xl border border-cyan-500/30 bg-black/40 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-black font-mono text-white">🔑 Primary Core Key</span>
                  <span className="px-1.5 py-0.2 rounded text-[9px] font-black bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">PAID</span>
                </div>
                <div className="text-[10px] text-slate-400 font-mono mb-2">Own model for Main & Text Traffic (WhatsApp • REST • Widget)</div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[9px] font-black uppercase tracking-wider text-slate-400 font-mono">Provider</span>
                  <select
                    value={providerFilter}
                    onChange={e => handleProviderChange(e.target.value as any)}
                    className="flex-1 bg-black/60 border border-purple-500/30 rounded-lg px-2 py-1 text-[10px] font-mono text-white outline-none focus:border-purple-400"
                  >
                    <option value="all">All Providers</option>
                    <option value="gemini">Google Gemini</option>
                    <option value="mimo">Xiaomi MiMo</option>
                  </select>
                </div>
                <select
                  value={selectedGlobalModel}
                  onChange={e => handleSelectGlobalModel(e.target.value)}
                  className="w-full bg-black/60 border border-cyan-500/30 rounded-xl px-3 py-2 text-xs font-mono text-white outline-none focus:border-cyan-400"
                >
                  {getSelectableModels(providerFilter, selectedGlobalModel).map(m => (
                    <option key={m.id} value={m.id}>{m.name.split(' (')[0]} — {m.badge}</option>
                  ))}
                </select>
                {selectedGlobalModel !== persistedGlobalModel ? (
                  <div className="text-[10px] text-amber-300 font-mono mt-1.5 flex items-center gap-1.5 animate-pulse">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-ping shrink-0" />
                    <span>⚠️ Unsaved change — Click Save to apply (Saved in DB: {modelPrettyName(persistedGlobalModel)})</span>
                  </div>
                ) : (
                  <div className="text-[10px] text-emerald-300 font-mono mt-1.5 flex items-center gap-1">
                    <span>✓ Active saved model in DB:</span>
                    <strong className="text-white font-mono">{modelPrettyName(persistedGlobalModel)}</strong>
                  </div>
                )}
              </div>

              {/* ── Profile 2: TRAINING VAULT KEY (FREE TIER) ── */}
              <div className="rounded-xl border border-emerald-500/30 bg-black/40 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-black font-mono text-white">🆓 Training Vault Key</span>
                  <span className="px-1.5 py-0.2 rounded text-[9px] font-black bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">FREE TIER</span>
                </div>
                <div className="text-[10px] text-slate-400 font-mono mb-2">Own model for Voice Calls (Luna WP Calls • Public/Tenet Web Call)</div>
                <select
                  value={selectedCallModel}
                  onChange={e => {
                    const val = e.target.value;
                    setSelectedCallModel(val);
                    setConfigs(prev => {
                      const updated = { ...prev };
                      if (updated['master_admin']) {
                        updated['master_admin'] = { ...updated['master_admin'], callAiModel: val };
                      }
                      if (updated[activeMode]) {
                        updated[activeMode] = { ...updated[activeMode], callAiModel: val };
                      }
                      return updated;
                    });
                  }}
                  className="w-full bg-black/60 border border-emerald-500/30 rounded-xl px-3 py-2 text-xs font-mono text-white outline-none focus:border-emerald-400"
                >
                  {[
                    { id: 'gemini-3.8-live', label: 'Gemini 3.8 Live (Thinking Audio)' },
                    { id: 'gemini-3.8-live-extended-thinking', label: 'Gemini 3.8 Live Extended Thinking' },
                    { id: 'gemini-3.1-flash-live-preview', label: 'Gemini 3.1 Flash Live Preview' },
                    { id: 'gemini-3.5-live-translate-preview', label: 'Gemini 3.5 Live Translate Preview' },
                    { id: 'gemini-3.5-transcribe-live', label: 'Gemini 3.5 Transcribe Live' },
                  ].map(m => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
                {selectedCallModel !== persistedCallModel ? (
                  <div className="text-[10px] text-amber-300 font-mono mt-1.5 flex items-center gap-1.5 animate-pulse">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-ping shrink-0" />
                    <span>⚠️ Unsaved change — Click Save to apply (Saved in DB: {modelPrettyName(persistedCallModel)})</span>
                  </div>
                ) : (
                  <div className="text-[10px] text-emerald-300 font-mono mt-1.5 flex items-center gap-1">
                    <span>✓ Active saved model in DB:</span>
                    <strong className="text-white font-mono">{modelPrettyName(persistedCallModel)}</strong>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-3">
              <button
                type="button"
                onClick={() => handleApplyGlobalModel()}
                disabled={applyingGlobal}
                className="px-4 py-2.5 rounded-xl font-black font-mono text-xs text-white bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 hover:opacity-90 active:scale-95 transition-all shadow-[0_0_25px_rgba(6,182,212,0.4)] disabled:opacity-50 flex items-center gap-2 shrink-0 cursor-pointer"
              >
                {applyingGlobal ? (
                  <>
                    <Cpu className="animate-spin" size={14} />
                    <span>Saving AI Models...</span>
                  </>
                ) : (
                  <>
                    <Zap size={14} />
                    <span>Save AI Model Globally (All Modes)</span>
                  </>
                )}
              </button>
              <span className="text-[10px] text-slate-500 font-mono">Saves models globally across all 8 operational modes and updates live AI sessions instantly.</span>
            </div>
          </div>
        </div>

        {/* Luna Mode Tabs */}
        <div className="px-6 py-3 border-b border-white/10 bg-black/40 flex items-center gap-2 overflow-x-auto shrink-0">
          {LUNA_MODES.map(mode => {
            const Icon = mode.icon;
            const isActive = activeMode === mode.id;
            const cfg = configs[mode.id];
            const status = cfg?.status || 'ACTIVE';
            return (
              <button
                key={mode.id}
                onClick={() => setActiveMode(mode.id)}
                className={`px-4 py-2.5 rounded-xl border text-xs font-bold font-mono transition-all flex items-center gap-2.5 shrink-0 ${
                  isActive
                    ? 'bg-gradient-to-r ' + mode.color + ' border-white/40 text-white shadow-lg shadow-purple-500/20 scale-[1.02]'
                    : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-slate-200'
                }`}
              >
                <Icon size={15} />
                <span>{mode.label}</span>
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase ${
                  status === 'ACTIVE' ? 'bg-black/40 text-emerald-300' : status === 'DISABLED' ? 'bg-red-500/40 text-red-200' : 'bg-amber-500/40 text-amber-200'
                }`}>
                  {status}
                </span>
              </button>
            );
          })}
        </div>

        {/* Main Content Body */}
        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center p-12 text-slate-500">
            <Cpu className="animate-spin text-cyan-400 mb-3" size={32} />
            <p className="text-sm font-mono">Loading Luna Matrix Configurations...</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-6">

            {/* Mode Overview Card */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-cyan-400 font-mono block mb-1">Mode Status</label>
                <select
                  value={activeConfig.status}
                  onChange={e => handleUpdateActiveConfig('status', e.target.value)}
                  className="w-full bg-black/60 border border-cyan-500/30 rounded-xl px-3 py-2 text-xs font-mono text-white outline-none focus:border-cyan-400"
                >
                  <option value="ACTIVE">✅ ACTIVE (Full AI Access)</option>
                  <option value="RESTRICTED">⚠️ RESTRICTED (Read-Only Guard)</option>
                  <option value="MAINTENANCE">🔧 MAINTENANCE (System Notice)</option>
                  <option value="DISABLED">⛔ DISABLED (Offline)</option>
                </select>
              </div>

              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-cyan-400 font-mono block mb-1">Assigned AI Model</label>
                <select
                  value={activeConfig.aiModel}
                  onChange={e => {
                    const val = e.target.value;
                    handleUpdateActiveConfig('aiModel', val);
                    if (activeMode === 'master_admin') {
                      handleSelectGlobalModel(val);
                    }
                  }}
                  className="w-full bg-black/60 border border-cyan-500/30 rounded-xl px-3 py-2 text-xs font-mono text-white outline-none focus:border-cyan-400"
                >
                  {getSelectableModels('all', activeConfig.aiModel).map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-cyan-400 font-mono block mb-1">
                    Max Output Tokens: <span className="text-white font-mono">{activeConfig.maxOutputTokens}</span>
                  </label>
                  <input
                    type="range"
                    min="100"
                    max="3000"
                    step="50"
                    value={activeConfig.maxOutputTokens}
                    onChange={e => handleUpdateActiveConfig('maxOutputTokens', parseInt(e.target.value))}
                    className="w-full accent-cyan-400 cursor-pointer"
                  />
                </div>
                <div className="w-24">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-cyan-400 font-mono block mb-1">
                    Temp: <span className="text-white font-mono">{activeConfig.temperature}</span>
                  </label>
                  <input
                    type="range"
                    min="0.0"
                    max="1.0"
                    step="0.05"
                    value={activeConfig.temperature}
                    onChange={e => handleUpdateActiveConfig('temperature', parseFloat(e.target.value))}
                    className="w-full accent-cyan-400 cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* Granular Capabilities & Permission Matrix */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <h3 className="text-sm font-bold text-white tracking-wide font-mono flex items-center gap-2 mb-4">
                <Sliders size={16} className="text-cyan-400" />
                PERMISSIONS & CAPABILITIES MATRIX ({activeConfig.displayName})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {PERMISSION_FLAGS.map(perm => {
                  const isEnabled = !!(activeConfig.allowedPermissions?.[perm.key]);
                  return (
                    <div
                      key={perm.key}
                      onClick={() => handleTogglePermission(perm.key)}
                      className={`p-3 rounded-xl border transition-all cursor-pointer flex items-start gap-3 ${
                        isEnabled
                          ? 'bg-cyan-500/10 border-cyan-500/40 text-cyan-200'
                          : 'bg-black/40 border-white/5 text-slate-500 hover:border-white/20'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={() => {}}
                        className="mt-0.5 accent-cyan-400 rounded cursor-pointer"
                      />
                      <div>
                        <p className={`text-xs font-bold font-mono ${isEnabled ? 'text-white' : 'text-slate-400'}`}>{perm.label}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5 leading-tight">{perm.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Editable Rules & Prompt Instructions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* Identity & Origin Box */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <UserCheck className="text-purple-400" size={18} />
                  <h4 className="text-xs font-bold text-white font-mono uppercase tracking-wider">Identity & Origin ("Who made Luna")</h4>
                </div>
                <div>
                  <label className="text-[10px] text-slate-400 block mb-1 font-mono">Creator & Origin Instruction</label>
                  <textarea
                    rows={3}
                    value={activeConfig.creatorInfo || ''}
                    onChange={e => handleUpdateActiveConfig('creatorInfo', e.target.value)}
                    placeholder="e.g. Created by Kangavena Cloud Platform (kangavena.com)..."
                    className="w-full bg-black/60 border border-white/10 rounded-xl p-3 text-xs font-mono text-slate-200 outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-400 block mb-1 font-mono">Platform Ownership & Entity Scope</label>
                  <textarea
                    rows={2}
                    value={activeConfig.ownershipInfo || ''}
                    onChange={e => handleUpdateActiveConfig('ownershipInfo', e.target.value)}
                    placeholder="e.g. Belongs to KangaVena POS & SaaS Platform..."
                    className="w-full bg-black/60 border border-white/10 rounded-xl p-3 text-xs font-mono text-slate-200 outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              {/* Capabilities ("What Luna CAN do") */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <Zap className="text-emerald-400" size={18} />
                  <h4 className="text-xs font-bold text-white font-mono uppercase tracking-wider">Capabilities ("What Luna CAN do")</h4>
                </div>
                <textarea
                  rows={6}
                  value={activeConfig.capabilitiesInstructions || ''}
                  onChange={e => handleUpdateActiveConfig('capabilitiesInstructions', e.target.value)}
                  placeholder="List specific actions Luna can execute in this mode..."
                  className="w-full bg-black/60 border border-white/10 rounded-xl p-3 text-xs font-mono text-emerald-200 outline-none focus:border-emerald-500"
                />
              </div>

              {/* Restrictions & Boundaries ("What Luna CANNOT do") */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <Lock className="text-rose-400" size={18} />
                  <h4 className="text-xs font-bold text-white font-mono uppercase tracking-wider">Restrictions & Security ("What Luna CANNOT do")</h4>
                </div>
                <textarea
                  rows={6}
                  value={activeConfig.restrictionsInstructions || ''}
                  onChange={e => handleUpdateActiveConfig('restrictionsInstructions', e.target.value)}
                  placeholder="List forbidden topics, privacy rules, and data access barriers..."
                  className="w-full bg-black/60 border border-white/10 rounded-xl p-3 text-xs font-mono text-rose-200 outline-none focus:border-rose-500"
                />
              </div>

              {/* Custom System Prompt Override */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="text-cyan-400" size={18} />
                    <h4 className="text-xs font-bold text-white font-mono uppercase tracking-wider">Full System Prompt Override</h4>
                  </div>
                  <button
                    onClick={() => setShowFullPromptPreview(!showFullPromptPreview)}
                    className="text-[10px] text-cyan-400 hover:underline font-mono flex items-center gap-1"
                  >
                    <Eye size={12} /> {showFullPromptPreview ? 'Hide Preview' : 'Toggle Preview'}
                  </button>
                </div>
                <textarea
                  rows={6}
                  value={activeConfig.customSystemPrompt || ''}
                  onChange={e => handleUpdateActiveConfig('customSystemPrompt', e.target.value)}
                  placeholder="Optional: Enter a complete custom system prompt template to override automatic prompt generation..."
                  className="w-full bg-black/60 border border-cyan-500/20 rounded-xl p-3 text-xs font-mono text-cyan-200 outline-none focus:border-cyan-500"
                />
              </div>

            </div>

          </div>
        )}

        {/* Footer Action Bar */}
        <div className="px-6 py-4 border-t border-white/10 bg-black/60 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2 text-xs font-mono text-slate-400">
            <Sparkles size={14} className="text-cyan-400" />
            <span>Mode: <strong className="text-white">{activeConfig.displayName}</strong></span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-700 bg-slate-800/50 text-slate-300 text-xs font-bold hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>

            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 border border-cyan-400/30 text-white text-xs font-black tracking-wide hover:shadow-[0_0_25px_rgba(6,182,212,0.4)] disabled:opacity-50 transition-all flex items-center gap-2"
            >
              {saving ? <Cpu className="animate-spin" size={15} /> : <Save size={15} />}
              SAVE & APPLY ALL (CONFIG & AI MODELS)
            </button>
          </div>
        </div>

      </div>
    );

    if (isInline) return content;

    return (
      <div className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        {content}
      </div>
    );
  };

export default LunaMasterConfigHub;
